import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, CircleHelp, Database, ExternalLink, FlaskConical, Plus, RefreshCw, RotateCcw, Trash2, Unlink, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type ReturnLinkedTenant, type ReturnSource, type ReturnsPortalTenant, type ReturnsShopifySettings } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { ClarificationsList } from "./Returns";
import { intlLocale } from "@/lib/i18n";

const errMsg = (e: unknown, f: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);
const selectCls = "h-8 w-full rounded-lg border border-border bg-surface px-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/10";
const norm = (s: string) => s.toLowerCase().replace(/gmbh|ug|haftungsbeschränkt|co\.? ?kg|&|\(|\)|\./g, "").replace(/\s+/g, " ").trim();

/** Logiful intern: Retouren-Bearbeitung (Retool-Datenbank) anbinden und Kunden zuordnen */
export function AdminReturnsPage() {
  const { me } = useTenant();
  const admin = me.user.staffRole === "staff_admin";
  const sources = useQuery({ queryKey: ["return-sources"], queryFn: api.admin.returnSources, refetchInterval: 30_000 });
  const [adding, setAdding] = useState(false);
  const [addingOps, setAddingOps] = useState(false);
  const retool = sources.data?.find((s) => s.kind === "retool");
  const operations = sources.data?.find((s) => s.kind === "operations");

  return (
    <div className="pb-16">
      <PageHeader
        title="Retouren-Anbindung"
        description="Retouren aus der Retouren-Bearbeitung (Retool) alle 5 Minuten übernehmen – je Kunde getrennt, verknüpft mit Aufträgen und Artikeln."
        actions={admin && !retool && !adding && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>Retool-Datenbank verbinden</Button>}
      />
      <div className="px-4 md:px-8 space-y-4 max-w-5xl">
        {adding && <NewSourceCard kind="retool" onDone={() => setAdding(false)} />}
        {sources.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !retool && !adding ? (
          <Card>
            <EmptyState icon={<Database className="size-5" />} title="Retouren-Bearbeitung noch nicht verbunden" action={admin ? <Button variant="primary" onClick={() => setAdding(true)}>Retool-Datenbank verbinden</Button> : undefined}>
              Verbindungs-URL aus Retool: Retool Database → Settings → Connection string. Am besten ein Datenbank-Benutzer nur mit Leserechten.
            </EmptyState>
          </Card>
        ) : retool ? (
          <>
            <SourceCard source={retool} admin={admin} />
            <TenantLinks source={retool} admin={admin} />
          </>
        ) : null}
        <OperationsSection source={operations} adding={addingOps} setAdding={setAddingOps} admin={admin} loading={sources.isLoading} />
        <ReturnsPortalLinks admin={admin} />
      </div>
    </div>
  );
}

const SOURCE_TEXT = {
  retool: { name: "Retouren-Bearbeitung (Retool)", title: "Retool-Datenbank verbinden", table: "retoure_app_test", done: "Jetzt die Kunden zuordnen – danach werden ihre Retouren übernommen." },
  operations: { name: "Klärfälle & Gründe (Operations)", title: "Operations-Datenbank verbinden", table: "returns.return_clarification_cases", done: "Klärfälle und Gründe werden ab jetzt alle 5 Minuten übernommen." },
} as const;

/** Verbindungs-URL aus Einzelfeldern – Benutzer/Passwort/Datenbank URL-kodiert (Sonderzeichen wie @ # / ? %) */
function buildPgUrl(f: { host: string; port: string; database: string; user: string; password: string; ssl: string }) {
  const enc = encodeURIComponent;
  const ssl = f.ssl === "disable" ? "" : `?sslmode=${f.ssl}`;
  return `postgresql://${enc(f.user.trim())}:${enc(f.password)}@${f.host.trim()}:${f.port.trim() || "5432"}/${enc(f.database.trim())}${ssl}`;
}

function NewSourceCard({ kind, onDone }: { kind: "retool" | "operations"; onDone: () => void }) {
  const qc = useQueryClient();
  const t = SOURCE_TEXT[kind];
  const [asUrl, setAsUrl] = useState(false);
  const [url, setUrl] = useState("");
  const [f, setF] = useState({ host: "", port: "5432", database: kind === "retool" ? "retool" : "operations", user: "", password: "", ssl: "require" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const fieldsOk = !!f.host.trim() && /^\d{2,5}$/.test(f.port.trim() || "5432") && !!f.database.trim() && !!f.user.trim() && !!f.password;
  const connectionUrl = asUrl ? url.trim() : buildPgUrl(f);
  const create = useMutation({
    mutationFn: () => api.admin.createReturnSource({ name: t.name, kind, connectionUrl }),
    onSuccess: () => {
      toast.success("Verbunden", { description: t.done });
      void qc.invalidateQueries({ queryKey: ["return-sources"] });
      onDone();
    },
    onError: (e) => toast.error("Verbindung fehlgeschlagen", { description: errMsg(e, "Bitte Angaben prüfen."), duration: 12_000 }),
  });
  const lbl = "text-[12px] font-medium text-fg-muted";
  return (
    <Card className="p-5">
      <h2 className="font-semibold text-[14px]">{t.title}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">Die Verbindung wird sofort geprüft (Tabelle {t.table}), verschlüsselt gespeichert und nie wieder angezeigt. Das Portal liest nur – bitte einen Datenbank-Benutzer nur mit Leserechten verwenden.</p>
      {asUrl ? (
        <label className="block mt-3"><span className={lbl}>Postgres-URL (Connection string)</span><Input type="password" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`postgresql://benutzer:passwort@host:5432/${f.database}?sslmode=require`} className="mt-1 font-mono" autoComplete="off" spellCheck={false} /><span className="block mt-1 text-[11.5px] text-fg-subtle">Sonderzeichen im Passwort müssen hier URL-kodiert sein (z. B. @ → %40) – einfacher mit den Einzelfeldern.</span></label>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_110px]">
          <label><span className={lbl}>Server (Host)</span><Input value={f.host} onChange={set("host")} placeholder="z. B. 178.63.119.27" className="mt-1 font-mono" autoComplete="off" spellCheck={false} /></label>
          <label><span className={lbl}>Port</span><Input value={f.port} onChange={set("port")} inputMode="numeric" className="mt-1 font-mono" /></label>
          <label><span className={lbl}>Datenbank</span><Input value={f.database} onChange={set("database")} className="mt-1 font-mono" autoComplete="off" spellCheck={false} /></label>
          <label><span className={lbl}>SSL</span>
            <select value={f.ssl} onChange={set("ssl")} className={cx(selectCls, "mt-1 h-9")}>
              <option value="require">erforderlich</option>
              <option value="no-verify">ohne Zertifikatsprüfung</option>
              <option value="disable">aus</option>
            </select>
          </label>
          <label><span className={lbl}>Benutzer</span><Input value={f.user} onChange={set("user")} placeholder="z. B. logiful_connect_ro" className="mt-1 font-mono" autoComplete="off" spellCheck={false} /></label>
          <label className="sm:col-span-1"><span className={lbl}>Passwort</span><Input type="password" value={f.password} onChange={set("password")} className="mt-1 font-mono" autoComplete="new-password" spellCheck={false} /></label>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button type="button" onClick={() => setAsUrl(!asUrl)} className="mr-auto text-[12px] text-fg-muted underline underline-offset-2 hover:text-fg">{asUrl ? "Einzelne Felder verwenden" : "Stattdessen URL einfügen"}</button>
        <Button variant="ghost" onClick={onDone}>Abbrechen</Button>
        <Button variant="primary" disabled={asUrl ? !/^postgres(ql)?:\/\//.test(url.trim()) : !fieldsOk} loading={create.isPending} onClick={() => create.mutate()}>Verbinden & prüfen</Button>
      </div>
    </Card>
  );
}

/** Operations-Datenbank: Klärfälle + Bezeichnungen der Gründe; dazu Klärfälle, die keinem Kunden zugeordnet werden konnten */
function OperationsSection({ source, adding, setAdding, admin, loading }: { source: ReturnSource | undefined; adding: boolean; setAdding: (v: boolean) => void; admin: boolean; loading: boolean }) {
  const unassigned = useQuery({ queryKey: ["clarifications-unassigned"], queryFn: api.admin.unassignedClarifications, enabled: !!source, staleTime: 60_000 });
  if (loading) return null;
  if (!source)
    return adding ? (
      <NewSourceCard kind="operations" onDone={() => setAdding(false)} />
    ) : (
      <Card className="p-5 flex flex-wrap items-center gap-3">
        <div className="size-10 rounded-xl bg-surface-2 grid place-items-center"><CircleHelp className="size-5 text-fg-muted" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Klärfälle & Gründe</p>
          <p className="text-[12.5px] text-fg-muted">Noch nicht verbunden – Klärfälle und die Bezeichnungen der Zustandsgründe liegen in der Operations-Datenbank.</p>
        </div>
        {admin && <Button variant="primary" onClick={() => setAdding(true)}>Operations-Datenbank verbinden</Button>}
      </Card>
    );
  return (
    <>
      <SourceCard source={source} admin={admin} />
      {!!unassigned.data?.length && (
        <section>
          <h2 className="font-semibold text-[14px] mb-2 flex items-center gap-2"><CircleHelp className="size-4 text-fg-muted" /> Klärfälle ohne Kunde <span className="text-[12px] font-normal text-warning tabular-nums">{unassigned.data.length}</span></h2>
          <ClarificationsList items={unassigned.data} loading={false} showUnassignedHint />
        </section>
      )}
    </>
  );
}

function SourceCard({ source, admin }: { source: ReturnSource; admin: boolean }) {
  const qc = useQueryClient();
  const sync = useMutation({ mutationFn: () => api.admin.syncReturnSource(source.id), onSuccess: () => toast.success("Abgleich gestartet") });
  const test = useMutation({
    mutationFn: () => api.admin.testReturnSource(source.id),
    onSuccess: (r) => { r.ok ? toast.success("Verbindung in Ordnung") : toast.error("Verbindung fehlgeschlagen", { description: r.error }); void qc.invalidateQueries({ queryKey: ["return-sources"] }); },
  });
  const remove = useMutation({ mutationFn: () => api.admin.deleteReturnSource(source.id), onSuccess: () => { toast.success("Verbindung entfernt"); void qc.invalidateQueries({ queryKey: ["return-sources"] }); } });
  const total = source.linkedTenants.reduce((s, l) => s + l.cases, 0);
  return (
    <Card className="p-5 flex flex-wrap items-start gap-3">
      <div className="size-10 rounded-xl bg-surface-2 grid place-items-center"><Database className="size-5 text-fg-muted" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{source.name}</p>
        <p className="text-[12.5px] text-fg-muted flex items-center gap-1.5">
          {source.status === "error" ? <CircleAlert className="size-3.5 text-danger shrink-0" /> : <CheckCircle2 className="size-3.5 text-success shrink-0" />}
          {source.status === "error" ? source.lastError : source.kind === "operations" ? `Letzter Abgleich ${fmtRelative(source.lastCheckedAt)} · Klärfälle und Gründe alle 5 Minuten` : `Letzter Abgleich ${fmtRelative(source.lastSyncAt)} · ${fmtNumber(total)} Retouren · ${source.linkedTenants.length} Kunden`}
        </p>
      </div>
      <Button size="sm" icon={<RefreshCw className="size-3.5" />} loading={sync.isPending} onClick={() => sync.mutate()}>Jetzt abgleichen</Button>
      <Button size="sm" variant="ghost" loading={test.isPending} onClick={() => test.mutate()}>Verbindung prüfen</Button>
      {admin && <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} loading={remove.isPending} onClick={() => confirm("Verbindung entfernen? Übernommene Retouren werden aus dem Portal gelöscht.") && remove.mutate()}>Entfernen</Button>}
    </Card>
  );
}

const ROW_GRID = "grid gap-x-6 gap-y-2.5 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1fr)] md:items-start";
/** Spaltenname über dem Feld – nur auf schmalen Bildschirmen, sonst steht er im Tabellenkopf */
const ColLabel = ({ children }: { children: string }) => <p className="md:hidden mb-1 text-[11.5px] font-medium text-fg-muted">{children}</p>;

function TenantLinks({ source, admin }: { source: ReturnSource; admin: boolean }) {
  const qc = useQueryClient();
  const tenants = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants });
  const customers = useQuery({ queryKey: ["return-customers", source.id], queryFn: () => api.admin.returnSourceCustomers(source.id), staleTime: 10 * 60_000, retry: false });
  const linkOf = useMemo(() => new Map(source.linkedTenants.map((l) => [l.tenantId, l])), [source.linkedTenants]);
  const usedBy = new Map(source.linkedTenants.map((l) => [l.key, l.name]));
  const save = useMutation({
    mutationFn: ({ tenantId, key }: { tenantId: string; key: string }) => api.admin.setReturnsLink(tenantId, key ? { sourceId: source.id, key } : null),
    onSuccess: (_r, v) => { toast.success(v.key ? "Zugeordnet – Retouren werden übernommen" : "Zuordnung gelöst"); void qc.invalidateQueries({ queryKey: ["return-sources"] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e, "") }),
  });
  /** Zuordnung über den Wawi-Verweis der Kundennummer (k_kunde = kKunde) – exakt, nie über Namen */
  const suggest = (refs: Record<string, string> | undefined) => {
    const key = refs?.["jtl-wawi"];
    return key ? customers.data?.customers.find((c) => c.key === key && !usedBy.has(c.key)) : undefined;
  };
  const pending = (tenants.data ?? []).map((t) => ({ t, s: !linkOf.get(t.id) ? suggest(t.refs) : undefined })).filter((x) => x.s);
  const linkAll = async () => {
    for (const { t, s } of pending) await save.mutateAsync({ tenantId: t.id, key: s!.key }).catch(() => undefined);
  };
  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <h2 className="font-semibold text-[14px] flex items-center gap-2"><Users className="size-4 text-fg-muted" /> Kunden zuordnen</h2>
        <p className="text-[12.5px] text-fg-muted">Welcher Kunde der Retouren-Bearbeitung zu welchem Portal-Kunden gehört – Vorschlag über die Kundennummer (Wawi). Kunden sehen ausschließlich ihre eigenen Retouren. Nach einer Änderung werden die Retouren dieses Kunden neu übernommen.</p>
        {admin && pending.length > 0 && (
          <Button size="sm" className="mt-2" loading={save.isPending} onClick={() => confirm(`${pending.length} Kunden über ihre Kundennummer zuordnen?`) && void linkAll()}>
            {pending.length} über Kundennummer zuordnen
          </Button>
        )}
      </div>
      {customers.isError && <p className="px-5 pt-3 text-[12.5px] text-danger">{errMsg(customers.error, "Kundenliste nicht lesbar.")}</p>}
      {/* Eine Zeile je Portal-Kunde, drei Spalten: wer · woher kommen die Retouren · wohin werden sie gemeldet */}
      <div className={cx(ROW_GRID, "hidden md:grid px-5 py-2 bg-surface-2/50 border-b border-border text-[11.5px] font-medium text-fg-muted")}>
        <span>Portal-Kunde</span>
        <span>Retouren-Bearbeitung (Retool)</span>
        <span>Geprüfte Retouren an Shopify melden</span>
      </div>
      <ul className="divide-y divide-border">
        {(tenants.data ?? []).map((t) => {
          const link = linkOf.get(t.id);
          const s = !link ? suggest(t.refs) : undefined;
          const linked = link ? customers.data?.customers.find((c) => c.key === link.key) : undefined;
          // Abgleich über die Kundennummer: Retool-Kunde und Portal-Kunde müssen dieselbe Kd-Nr. tragen
          const match = !linked?.number || !t.customerNumber ? null : String(linked.number) === String(t.customerNumber);
          return (
            <li key={t.id} className={cx(ROW_GRID, "px-5 py-3 text-[13px]", match === false && !t.testModeSince && "bg-warning-soft/40")}>
              <div className="min-w-0">
                <p className="font-medium truncate">{t.name}</p>
                <p className="text-[11.5px] text-fg-subtle tabular-nums">
                  {t.customerNumber ? `Kd-Nr. ${t.customerNumber}` : "ohne Kd-Nr."}
                  {link && ` · ${fmtNumber(link.cases)} Retouren übernommen`}
                </p>
              </div>

              <div className="min-w-0">
                <ColLabel>Retouren-Bearbeitung (Retool)</ColLabel>
                <select className={selectCls} value={link?.key ?? ""} disabled={!admin || save.isPending} onChange={(e) => save.mutate({ tenantId: t.id, key: e.target.value })}>
                  <option value="">– nicht zugeordnet –</option>
                  {link && !linked && <option value={link.key}>Kundennr. {link.key}</option>}
                  {customers.data?.customers.map((c) => (
                    <option key={c.key} value={c.key} disabled={usedBy.has(c.key) && usedBy.get(c.key) !== t.name}>{c.name}{c.number ? ` · Kd-Nr. ${c.number}` : ""} ({fmtNumber(c.lines)} Pos.){usedBy.has(c.key) && usedBy.get(c.key) !== t.name ? ` – ${usedBy.get(c.key)}` : ""}</option>
                  ))}
                </select>
                {match === true && <p className="mt-1 text-[11.5px] text-success flex items-center gap-1"><CheckCircle2 className="size-3.5" /> Kd-Nr. stimmt überein</p>}
                {/* Testkunden nutzen bewusst die Retouren eines Produktivkunden (nur lesend) – kein Fehler */}
                {match === false && t.testModeSince && (
                  <p className="mt-1 text-[11.5px] text-fg-muted flex items-start gap-1">
                    <FlaskConical className="size-3.5 shrink-0 mt-px" />
                    <span>Testkunde – nutzt die Retouren von Kd-Nr. {linked!.number}</span>
                  </p>
                )}
                {match === false && !t.testModeSince && (
                  <p className="mt-1 text-[11.5px] text-warning flex items-start gap-1">
                    <CircleAlert className="size-3.5 shrink-0 mt-px" />
                    <span>Kd-Nr. weicht ab: Retool {linked!.number} ≠ Portal {t.customerNumber} – Zuordnung oder Kundennummer prüfen</span>
                  </p>
                )}
                {s && admin && (
                  <button className="mt-1 text-[11.5px] text-fg-muted hover:text-fg underline underline-offset-2" onClick={() => save.mutate({ tenantId: t.id, key: s.key })}>
                    Vorschlag laut Kd-Nr.: {s.name} übernehmen
                  </button>
                )}
              </div>

              <div className="min-w-0">
                <ColLabel>An Shopify melden</ColLabel>
                {link ? <ShopifyReturnSetting link={link} admin={admin} /> : <p className="text-[12px] text-fg-subtle md:pt-1.5">erst Retool-Kunden zuordnen</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

const MODE_LABEL: Record<ReturnsShopifySettings["mode"], string> = {
  connect: "über Logiful Connect",
  own_app: "über die eigene Shopify-App",
  off: "nicht melden",
};

/** Je Kunde: wer meldet geprüfte Retouren an Shopify – nie zwei Wege gleichzeitig (sonst doppelte Buchung/Erstattung) */
function ShopifyReturnSetting({ link, admin }: { link: ReturnLinkedTenant; admin: boolean }) {
  const qc = useQueryClient();
  const current = link.shopify;
  // Vorschlag, solange nichts gewählt ist: meldet die eigene App schon, bleibt es dabei – sonst Connect
  const suggested: ReturnsShopifySettings["mode"] = link.ownAppSeen ? "own_app" : "connect";
  const save = useMutation({
    mutationFn: (v: { mode: ReturnsShopifySettings["mode"]; autoRefund: boolean; reportUndeliverable?: boolean }) => api.admin.setReturnsShopify(link.tenantId, v),
    onSuccess: (r) => {
      toast.success(`Retouren an Shopify: ${MODE_LABEL[r.mode]}`, { description: r.mode === "connect" ? "Gilt für ab jetzt geprüfte Retouren." : undefined });
      void qc.invalidateQueries({ queryKey: ["return-sources"] });
    },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e, "") }),
  });
  const mode = current?.mode;
  return (
    <div className="space-y-1 text-[12.5px]">
      <select
        className={selectCls}
        value={mode ?? ""}
        disabled={!admin || save.isPending}
        onChange={(e) => e.target.value && save.mutate({ mode: e.target.value as ReturnsShopifySettings["mode"], autoRefund: current?.autoRefund ?? false, reportUndeliverable: current?.reportUndeliverable ?? false })}
      >
        {!mode && <option value="">– noch nicht festgelegt –</option>}
        {(["connect", "own_app", "off"] as const).map((m) => (
          <option key={m} value={m}>{MODE_LABEL[m]}{!mode && m === suggested ? " (Vorschlag)" : ""}</option>
        ))}
      </select>
      {mode === "connect" && (
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" className="size-3.5 accent-current" checked={current!.autoRefund} disabled={!admin || save.isPending} onChange={(e) => save.mutate({ mode: "connect", autoRefund: e.target.checked, reportUndeliverable: current!.reportUndeliverable ?? false })} />
          Automatisch erstatten
        </label>
      )}
      {mode === "connect" && (
        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Rückläufer (Sendung nicht zugestellt) ebenfalls als Retoure in Shopify buchen – z. B. damit der Kunde erstatten kann">
          <input type="checkbox" className="size-3.5 accent-current" checked={!!current!.reportUndeliverable} disabled={!admin || save.isPending} onChange={(e) => save.mutate({ mode: "connect", autoRefund: current!.autoRefund, reportUndeliverable: e.target.checked })} />
          Rückläufer ebenfalls melden
        </label>
      )}
      <span className="block text-[11.5px] text-fg-subtle">
        {mode === "connect" ? (
          <>
            seit {new Date(current!.activeSince).toLocaleDateString(intlLocale())} · {fmtNumber(link.shopifyDone)} gemeldet
            {link.shopifyFailed > 0 && <span className="text-danger" title={link.shopifyLastError ?? undefined}> · {fmtNumber(link.shopifyFailed)} fehlgeschlagen</span>}
          </>
        ) : mode === "own_app" ? (
          "Connect meldet nichts – die eigene App des Kunden übernimmt."
        ) : !mode && link.ownAppSeen ? (
          "Die eigene Shopify-App des Kunden meldet bereits Retouren."
        ) : null}
      </span>
    </div>
  );
}

/**
 * Retourenportal (eigener Dienst): welcher Connect-Kunde gehört zu welchem Mandanten dort.
 * Bestandskunden verknüpfen, neue Mandanten anlegen, Zuordnung lösen – danach springen Kunden direkt in ihr Portal.
 */
function ReturnsPortalLinks({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const portal = useQuery({ queryKey: ["returns-portal-admin"], queryFn: api.admin.returnsPortal, staleTime: 30_000 });
  const tenants = useQuery({ queryKey: ["admin-tenants"], queryFn: api.admin.tenants });
  const save = useMutation({
    mutationFn: ({ tenantId, body }: { tenantId: string; body: Parameters<typeof api.admin.setReturnsPortal>[1] }) => api.admin.setReturnsPortal(tenantId, body),
    onSuccess: (_r, v) => {
      toast.success(v.body.mode === "unlink" ? "Zuordnung gelöst" : v.body.mode === "new" ? "Retourenportal angelegt und verknüpft" : "Verknüpft");
      void qc.invalidateQueries({ queryKey: ["returns-portal-admin"] });
      void qc.invalidateQueries({ queryKey: ["returns-portal"] });
    },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e, "") }),
  });
  const open = async (tenantId: string) => {
    const win = window.open("about:blank", "_blank");
    try {
      const { url } = await api.returns.openPortal(tenantId);
      if (win) win.location.href = url;
    } catch (e) {
      win?.close();
      toast.error("Retourenportal nicht erreichbar", { description: errMsg(e, "") });
    }
  };
  const d = portal.data;
  if (portal.isLoading) return <Skeleton className="h-40 w-full" />;
  if (!d?.configured) return null;
  const byConnect = new Map(d.tenants.filter((p) => p.connectTenantId).map((p) => [p.connectTenantId!, p]));
  const byNumber = new Map(d.tenants.filter((p) => p.customerNumber).map((p) => [p.customerNumber!, p]));
  const linkedOf = (t: { id: string; customerNumber?: string | null }) => byConnect.get(t.id) ?? (t.customerNumber ? byNumber.get(t.customerNumber) : undefined);
  const free = d.tenants.filter((p) => !p.connectTenantId && !p.customerNumber);
  const linkedCount = (tenants.data ?? []).filter((t) => linkedOf(t)).length;
  const suggest = (name: string) => {
    const n = norm(name);
    return free.find((p) => n && (norm(p.name).includes(n) || n.includes(norm(p.name))));
  };
  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <h2 className="font-semibold text-[14px] flex items-center gap-2"><RotateCcw className="size-4 text-fg-muted" /> Retourenportal</h2>
        <p className="text-[12.5px] text-fg-muted">
          Welcher Kunde zu welchem Mandanten im Retourenportal gehört. Verknüpfte Kunden springen aus dem Kundenportal direkt in ihr Retourenportal, ohne zweiten Login.
          {d.reachable && <> · {fmtNumber(linkedCount)} von {fmtNumber(tenants.data?.length ?? 0)} Kunden verknüpft{free.length ? ` · ${fmtNumber(free.length)} Mandanten noch ohne Kunde` : ""}</>}
        </p>
      </div>
      {!d.reachable ? (
        <p className="px-5 py-4 text-[12.5px] text-danger flex items-center gap-1.5"><CircleAlert className="size-3.5 shrink-0" /> {d.error ?? "Retourenportal nicht erreichbar."}</p>
      ) : (
        <ul className="divide-y divide-border">
          {(tenants.data ?? []).map((t) => {
            const linked = linkedOf(t);
            const s = !linked ? suggest(t.name) : undefined;
            const busy = save.isPending && save.variables?.tenantId === t.id;
            return (
              <li key={t.id} className="px-5 py-2.5 flex flex-wrap items-center gap-3 text-[13px]">
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{t.name}</span>
                  {t.customerNumber && <span className="ml-1.5 text-[11.5px] text-fg-subtle tabular-nums">Kd-Nr. {t.customerNumber}</span>}
                  {linked ? (
                    <span className="block text-[11.5px] text-fg-subtle">
                      {linked.name} · {linked.setupDone ? "Shop verbunden" : <span className="text-warning">Shop noch nicht verbunden</span>} · {fmtNumber(linked.openReturns)} offen · {fmtNumber(linked.returns30d)} in 30 Tagen
                      {!linked.active && <span className="text-danger"> · inaktiv</span>}
                    </span>
                  ) : (
                    <span className="block text-[11.5px] text-fg-subtle">nicht verknüpft</span>
                  )}
                </span>
                {linked ? (
                  <>
                    <Button size="sm" variant="ghost" icon={<ExternalLink className="size-3.5" />} onClick={() => void open(t.id)}>Öffnen</Button>
                    {admin && (
                      <Button size="sm" variant="ghost" icon={<Unlink className="size-3.5" />} loading={busy} onClick={() => confirm(`Zuordnung von „${t.name}“ zu „${linked.name}“ lösen? Der Mandant im Retourenportal bleibt erhalten.`) && save.mutate({ tenantId: t.id, body: { mode: "unlink" } })}>
                        Lösen
                      </Button>
                    )}
                  </>
                ) : admin && !t.customerNumber ? (
                  <span className="text-[12px] text-warning">Erst Kundennummer festlegen (Admin → Kunden)</span>
                ) : admin ? (
                  <>
                    {s && <Button size="sm" variant="ghost" loading={busy} onClick={() => save.mutate({ tenantId: t.id, body: { mode: "link", portalTenantId: s.tenantId } })}>Vorschlag: {s.name}</Button>}
                    <select
                      className={cx(selectCls, "w-[260px]")}
                      value=""
                      disabled={busy}
                      aria-label={`Retourenportal für ${t.name} zuordnen`}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__new") {
                          if (confirm(`Für „${t.name}“ ein neues Retourenportal anlegen?`)) save.mutate({ tenantId: t.id, body: { mode: "new" } });
                        } else if (v) save.mutate({ tenantId: t.id, body: { mode: "link", portalTenantId: v } });
                      }}
                    >
                      <option value="">– Mandant wählen –</option>
                      {free.map((p: ReturnsPortalTenant) => (
                        <option key={p.tenantId} value={p.tenantId}>{p.name} ({fmtNumber(p.returns30d)} Retouren in 30 T.)</option>
                      ))}
                      <option value="__new">+ Neu anlegen</option>
                    </select>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
